import { exists, SystemCode, SystemCodeFromJSON } from '@/api'
import { defaultSystemCode } from '@/api/models/system-code/SystemCode'
import {
  dateTimeToDateFormat,
  dateTimeToPlainFormat,
  dateToPlainFormat
} from '@/utils'
import { BibliographyClass, CollectionFlag, Prefix } from '@/types'

/**
 * 予約連絡情報の予約
 */
export interface ReserveOfReserveContactInfo {
  id: number
  registrationBranchCode: SystemCode
  registrationPlaceCode: SystemCode
  registrationTime: string
  forwardingBranchCode: SystemCode
  forwardingPlaceCode: SystemCode
  specifyCollectionId: number
  specifyCollectionNo: string
  reserveSpecifyBranchCodes: SystemCode[]
  reserveStatus: SystemCode
  reserveNoticeAllowed: boolean
  contactMethod: SystemCode
  reserveContactTime: string
  preferredDate: string
  reserveExpiration: string
  forwardLevel: SystemCode
  forwardRequestBranchCodes: SystemCode[]
  forwardMemo: string
  displayMemo: string
  revision: number
  reserveContactCompleted: boolean
  reserveContactCount: number
}

export const defaultReserveOfReserveContactInfo = (): ReserveOfReserveContactInfo => ({
  id: 0,
  registrationBranchCode: defaultSystemCode(),
  registrationPlaceCode: defaultSystemCode(),
  registrationTime: '',
  forwardingBranchCode: defaultSystemCode(),
  forwardingPlaceCode: defaultSystemCode(),
  specifyCollectionId: 0,
  specifyCollectionNo: '',
  reserveSpecifyBranchCodes: [],
  reserveStatus: defaultSystemCode(),
  reserveNoticeAllowed: false,
  contactMethod: defaultSystemCode(),
  reserveContactTime: '',
  preferredDate: '',
  reserveExpiration: '',
  forwardLevel: defaultSystemCode(),
  forwardRequestBranchCodes: [],
  forwardMemo: '',
  displayMemo: '',
  revision: 0,
  reserveContactCompleted: false,
  reserveContactCount: 0
})

export function ReserveOfReserveContactInfoFromJSON(
  json: any
): ReserveOfReserveContactInfo {
  if (json === undefined || json === null) return json

  return {
    id: exists(json, 'id') ? json.id : 0,
    registrationBranchCode: exists(json, 'registration_branch_code')
      ? SystemCodeFromJSON(json.registration_branch_code)
      : defaultSystemCode(),
    registrationPlaceCode: exists(json, 'registration_place_code')
      ? SystemCodeFromJSON(json.registration_place_code)
      : defaultSystemCode(),
    registrationTime: exists(json, 'registration_time')
      ? dateTimeToDateFormat(dateTimeToPlainFormat(json.registration_time))
      : '',
    forwardingBranchCode: exists(json, 'forwarding_branch_code')
      ? SystemCodeFromJSON(json.forwarding_branch_code)
      : defaultSystemCode(),
    forwardingPlaceCode: exists(json, 'forwarding_place_code')
      ? SystemCodeFromJSON(json.forwarding_place_code)
      : defaultSystemCode(),
    specifyCollectionId: exists(json, 'specify_collection_id')
      ? json.specify_collection_id
      : 0,
    specifyCollectionNo: exists(json, 'specify_collection_no')
      ? json.specify_collection_no
      : '',
    reserveSpecifyBranchCodes: exists(json, 'reserve_specify_branch_codes')
      ? json.reserve_specify_branch_codes.map(SystemCodeFromJSON)
      : [],
    reserveStatus: exists(json, 'reserve_status')
      ? SystemCodeFromJSON(json.reserve_status)
      : defaultSystemCode(),
    reserveNoticeAllowed: exists(json, 'reserve_notice_allowed')
      ? json.reserve_notice_allowed
      : false,
    contactMethod: exists(json, 'contact_method')
      ? SystemCodeFromJSON(json.contact_method)
      : defaultSystemCode(),
    reserveContactTime: exists(json, 'reserve_contact_time')
      ? dateTimeToPlainFormat(json.reserve_contact_time)
      : '',
    preferredDate: exists(json, 'preferred_date')
      ? dateToPlainFormat(json.preferred_date)
      : '',
    reserveExpiration: exists(json, 'reserve_expiration')
      ? dateToPlainFormat(json.reserve_expiration)
      : '',
    forwardLevel: exists(json, 'forward_level')
      ? SystemCodeFromJSON(json.forward_level)
      : defaultSystemCode(),
    forwardRequestBranchCodes: exists(json, 'forward_request_branch_codes')
      ? json.forward_request_branch_codes.map(SystemCodeFromJSON)
      : [],
    forwardMemo: exists(json, 'forward_memo') ? json.forward_memo : '',
    displayMemo: exists(json, 'display_memo') ? json.display_memo : '',
    revision: exists(json, 'revision') ? json.revision : 0,
    reserveContactCompleted: exists(json, 'reserve_contact_completed')
      ? json.reserve_contact_completed
      : false,
    reserveContactCount: exists(json, 'reserve_contact_count')
      ? json.reserve_contact_count
      : 0
  }
}

/**
 * 予約連絡情報の利用者
 */
export interface UserOfReserveContactInfo {
  id: string
  name: string
  nameKana: string
  userType: SystemCode
  phoneType: SystemCode
  phoneNumber: string
  revision: number
}

export const defaultUserOfReserveContactInfo = (): UserOfReserveContactInfo => ({
  id: '',
  name: '',
  nameKana: '',
  userType: defaultSystemCode(),
  phoneType: defaultSystemCode(),
  phoneNumber: '',
  revision: 0
})

export function UserOfReserveContactInfoFromJSON(
  json: any
): UserOfReserveContactInfo {
  return UserOfReserveContactInfoFromJSONTyped(json)
}

export function UserOfReserveContactInfoFromJSONTyped(
  json: any
): UserOfReserveContactInfo {
  if (json === undefined || json === null) return json

  return {
    id: exists(json, 'id') ? json.id : '',
    name: exists(json, 'name') ? json.name : '',
    nameKana: exists(json, 'name_kana') ? json.name_kana : '',
    userType: exists(json, 'user_type')
      ? SystemCodeFromJSON(json.user_type)
      : defaultSystemCode(),
    phoneType: exists(json, 'phone_type')
      ? SystemCodeFromJSON(json.phone_type)
      : defaultSystemCode(),
    phoneNumber: exists(json, 'phone_number') ? json.phone_number : '',
    revision: exists(json, 'revision') ? json.revision : 0
  }
}

/**
 * 予約連絡情報の資料
 */
export interface CollectionOfReserveContactInfo {
  id: number
  collectionNo: string
  holdingBranchCode: SystemCode
  callNumber: string
  shelfType: SystemCode
  collectionType: SystemCode
  status: SystemCode
  handlingType: SystemCode
  appendixCode: SystemCode
  forwardingBranchCode: SystemCode
  updatedTime: string
  updatedBranchCode: SystemCode
  revision: number
}

export const defaultCollectionOfReserveContactInfo = (): CollectionOfReserveContactInfo => ({
  id: 0,
  collectionNo: '',
  holdingBranchCode: defaultSystemCode(),
  callNumber: '',
  shelfType: defaultSystemCode(),
  collectionType: defaultSystemCode(),
  status: defaultSystemCode(),
  handlingType: defaultSystemCode(),
  appendixCode: defaultSystemCode(),
  forwardingBranchCode: defaultSystemCode(),
  updatedTime: '',
  updatedBranchCode: defaultSystemCode(),
  revision: 0
})

export function CollectionOfReserveContactInfoFromJSON(
  json: any
): CollectionOfReserveContactInfo {
  return {
    id: exists(json, 'id') ? json.id : 0,
    collectionNo: exists(json, 'collection_no') ? json.collection_no : '',
    holdingBranchCode: exists(json, 'holding_branch_code')
      ? SystemCodeFromJSON(json.holding_branch_code)
      : defaultSystemCode(),
    callNumber: exists(json, 'call_number') ? json.call_number : '',
    shelfType: exists(json, 'shelf_type')
      ? SystemCodeFromJSON(json.shelf_type)
      : defaultSystemCode(),
    collectionType: exists(json, 'collection_type')
      ? SystemCodeFromJSON(json.collection_type)
      : defaultSystemCode(),
    status: exists(json, 'status')
      ? SystemCodeFromJSON(json.status)
      : defaultSystemCode(),
    handlingType: exists(json, 'handling_type')
      ? SystemCodeFromJSON(json.handling_type)
      : defaultSystemCode(),
    appendixCode: exists(json, 'appendix_code')
      ? SystemCodeFromJSON(json.appendix_code)
      : defaultSystemCode(),
    forwardingBranchCode: exists(json, 'forwarding_branch_code')
      ? SystemCodeFromJSON(json.forwarding_branch_code)
      : defaultSystemCode(),
    updatedTime: exists(json, 'updated_time')
      ? dateTimeToDateFormat(dateTimeToPlainFormat(json.updated_time))
      : '',
    updatedBranchCode: exists(json, 'updated_branch_code')
      ? SystemCodeFromJSON(json.updated_branch_code)
      : defaultSystemCode(),
    revision: exists(json, 'revision') ? json.revision : 0
  }
}

/**
 * 予約連絡情報の書誌
 */
export interface BibliographyOfReserveContactInfo {
  bibliographyClass: BibliographyClass
  prefix: Prefix
  bibliographyId: number
  searchType: SystemCode
  displayMaterialFormat: SystemCode
  displayClass: string
  displayTitle: string
  displayAuthor: string
  volume: string
  volumeNumber: string
  volumeTotal: string
  volumeDateLabel: string
  volumeSpecial: string
  displayPublisher: string
  bibliographySetId: number
  holdingCount: number
  orderCount: number
  reserveCount: number
  collectionFlag: CollectionFlag
  revision: number
}

export const defaultBibliographyOfReserveContactInfo = (): BibliographyOfReserveContactInfo => ({
  bibliographyClass: BibliographyClass.Real,
  prefix: Prefix.Real,
  bibliographyId: 0,
  searchType: defaultSystemCode(),
  displayMaterialFormat: defaultSystemCode(),
  displayClass: '',
  displayTitle: '',
  displayAuthor: '',
  volume: '',
  volumeNumber: '',
  volumeTotal: '',
  volumeDateLabel: '',
  volumeSpecial: '',
  displayPublisher: '',
  bibliographySetId: 0,
  holdingCount: 0,
  orderCount: 0,
  reserveCount: 0,
  collectionFlag: CollectionFlag.NotCollection,
  revision: 0
})

export function BibliographyOfReserveContactInfoFromJSON(
  json: any
): BibliographyOfReserveContactInfo {
  return BibliographyOfReserveContactInfoFromJSONTyped(json)
}

export function BibliographyOfReserveContactInfoFromJSONTyped(
  json: any
): BibliographyOfReserveContactInfo {
  if (json === undefined || json === null) return json

  return {
    bibliographyClass: exists(json, 'bibliography_class')
      ? json.bibliography_class
      : BibliographyClass.Real,
    prefix: exists(json, 'prefix') ? json.prefix : Prefix.Real,
    bibliographyId: exists(json, 'bibliography_id') ? json.bibliography_id : 0,
    searchType: exists(json, 'search_type')
      ? SystemCodeFromJSON(json.search_type)
      : defaultSystemCode(),
    displayMaterialFormat: exists(json, 'display_material_format')
      ? SystemCodeFromJSON(json.display_material_format)
      : defaultSystemCode(),
    displayClass: exists(json, 'display_class') ? json.display_class : '',
    displayTitle: exists(json, 'display_title') ? json.display_title : '',
    displayAuthor: exists(json, 'display_author') ? json.display_author : '',
    volume: exists(json, 'volume') ? json.volume : '',
    volumeNumber: exists(json, 'volume_number') ? json.volume_number : '',
    volumeTotal: exists(json, 'volume_total') ? json.volume_total : '',
    volumeDateLabel: exists(json, 'volume_date_label')
      ? json.volume_date_label
      : '',
    volumeSpecial: exists(json, 'volume_special') ? json.volume_special : '',
    displayPublisher: exists(json, 'display_publisher')
      ? json.display_publisher
      : '',
    bibliographySetId: exists(json, 'bibliography_set_id')
      ? json.bibliography_set_id
      : '0',
    holdingCount: exists(json, 'holding_count') ? json.holding_count : 0,
    orderCount: exists(json, 'order_count') ? json.order_count : 0,
    reserveCount: exists(json, 'reserve_count') ? json.reserve_count : 0,
    collectionFlag: exists(json, 'collection_flag')
      ? json.collection_flag
      : CollectionFlag.NotCollection,
    revision: exists(json, 'revision') ? json.revision : 0
  }
}

/**
 * 予約連絡情報
 */
export interface ReserveContactInfo {
  reserve: ReserveOfReserveContactInfo
  user: UserOfReserveContactInfo
  collection: CollectionOfReserveContactInfo
  bibliography: BibliographyOfReserveContactInfo
}

export const defaultReserveContactInfo = (): ReserveContactInfo => ({
  reserve: defaultReserveOfReserveContactInfo(),
  user: defaultUserOfReserveContactInfo(),
  collection: defaultCollectionOfReserveContactInfo(),
  bibliography: defaultBibliographyOfReserveContactInfo()
})

export function ReserveContactInfoFromJSON(json: any): ReserveContactInfo {
  return {
    reserve: exists(json, 'reserve')
      ? ReserveOfReserveContactInfoFromJSON(json.reserve)
      : defaultReserveOfReserveContactInfo(),
    user: exists(json, 'user')
      ? UserOfReserveContactInfoFromJSON(json.user)
      : defaultUserOfReserveContactInfo(),
    collection: exists(json, 'collection')
      ? CollectionOfReserveContactInfoFromJSON(json.collection)
      : defaultCollectionOfReserveContactInfo(),
    bibliography: exists(json, 'bibliography')
      ? BibliographyOfReserveContactInfoFromJSON(json.bibliography)
      : defaultBibliographyOfReserveContactInfo()
  }
}
